import { useTheme } from "@emotion/react";
import { Box, Button, CircularProgress, Typography, useMediaQuery } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import Popup from "../../components/popups/popup";
import BranchDropdown from "../../components/dropdowns/branchDropdown";
import CustomInput from "../../components/inputs/customInput";
import ImageUpload from "../../components/inputs/uploadImage";
import AddIcon from '@mui/icons-material/Add';
import PageService from "../../services/pageService";
import CityPageListForm from "../../components/subFroms/cityPageListForm";

const AddPage = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    const [selectedBranch, setSelectedBranch] = useState(null);
    const [selectedImage, setSelectedImage] = useState(null);
    const [selectedIDImage, setSelectedIDImage] = useState(null);

    const [showPopup, setShowPopup] = useState(false);
    const [popupMsg, setPopupMsg] = useState(false);
    const [popupType, setPopupType] = useState(false);

    const [formValues, setFormValues] = useState({
        name: '',
        ownerName: '',
        phone: '',
        phoneTwo: '',
        password: '',
        confirmPassword: '',
        instgram: '',
        facebook: '',
        note: '',
        branchId: null,
        prices: [],
    });


    const [addErrors, setAddErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormValues((prevValues) => ({
            ...prevValues,
            [name]: value,
        }));
    };

    const handleImageChange = (file) => {
        setSelectedImage(file);
    };

    const handleIDImageChange = (file) => {
        setSelectedIDImage(file);
    };

    const validateForm = () => {
        const newAddErrors = {};

        if (!formValues.name) {
            newAddErrors.name = t('nameRequired');
        }

        if (!formValues.ownerName) {
            newAddErrors.ownerName = t('ownerNameRequired');
        }

        if (!formValues.phone) {
            newAddErrors.phone = t('phoneRequired');
        }

        if (!formValues.password) {
            newAddErrors.password = t('passwordRequired');
        }

        if (formValues.password.length < 8 && formValues.password) {
            newAddErrors.password = t('passwordMin');
        }

        if (!formValues.confirmPassword) {
            newAddErrors.confirmPassword = t('confirmPasswordRequired');
        }

        if (formValues.password !== formValues.confirmPassword) {
            newAddErrors.confirmPassword = t('confirmPasswordMatch');
        }

        if (!formValues.branchId) {
            newAddErrors.branch = t('branchRequired');
        }

        formValues.prices.forEach((price, index) => {
            if (!price.cityId) price.cityError = t('cityRequired');
            else price.cityError = "";
            if (!price.transportationPrice) price.transportationPriceError = t('transportationPriceRequired');
            else price.transportationPriceError = "";
            if (isNaN(price.transportationPrice)) price.transportationPriceError = t('transportationPriceNumber');
        });

        setAddErrors(newAddErrors);

        const checkError = formValues.prices.some((price) => (price.cityError !== "" || price.transportationPriceError !== ""));

        return Object.keys(newAddErrors).length === 0 && !checkError;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (isLoading) return;

        if (validateForm()) {
            try {
                setIsLoading(true);
                const formData = new FormData();
                const { prices, ...formInfo } = formValues;

                for (const key in formInfo) {
                    if (formInfo[key] && formInfo[key] !== '') {
                        formData.append(key, formInfo[key]);
                    }
                }

                prices.forEach((price, index) => {
                    formData.append(`cities[${index}][cityId]`, price.cityId);
                    formData.append(`cities[${index}][transportationPrice]`, price.transportationPrice);
                });

                if (selectedImage) {
                    formData.append('image', selectedImage);
                }

                if (selectedIDImage) {
                    formData.append('IDImage', selectedIDImage);
                }

                const data = await PageService.add(formData);
                if (data.status === 200) {
                    setPopupMsg(data.data.message);
                    setPopupType('success');
                    setShowPopup(true);
                    setFormValues({
                        name: '',
                        phone: '', phoneTwo: '',
                        password: '', confirmPassword: '',
                        ownerName: '',
                        instgram: '',
                        facebook: '',
                        note: '',
                        prices: []
                    });
                    setSelectedBranch(null);
                    setSelectedImage(null);
                    setSelectedIDImage(null);
                }

                setAddErrors({});
            } catch (error) {
                if (error.response?.status === 400) {
                    const responseErrors = error.response.data || {};
                    const formattedErrors = {};

                    Object.keys(responseErrors).forEach((field) => {
                        formattedErrors[field] = responseErrors[field];
                    });

                    setAddErrors(formattedErrors);
                } else {
                    setPopupMsg(error.response.data.error);
                    setPopupType('error');
                    console.error("An unexpected error occurred:", error);
                    setAddErrors({ general: error.response.data.error });
                }
            } finally {
                setIsLoading(false);
            }
        }
    };


    return (
        <Box sx={{
            p: isTablet ? 1 : 2,
            backgroundColor: theme.palette.secondary.main,
        }}>

            {showPopup && (
                <Popup
                    text={popupMsg}
                    type={popupType}
                    onClose={() => setShowPopup(false)}
                />
            )}
            <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{
                    width: '100%',
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    border: 1,
                    borderColor: theme.palette.customGrey.borderInput,
                    p: isTablet ? 2 : 4
                }}
            >

                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between'
                }}>
                    <Typography id="transition-modal-title" variant="h6" component="h2">
                        {t('addPage')}
                    </Typography>
                </Box>


                {/* row 1 */}
                <Box sx={{
                    display: isTablet ? 'unset' : 'flex',
                    justifyContent: 'space-between'
                }}>
                    {/* Branch */}
                    <Box width={isTablet ? '100%' : '32%'}>
                        <BranchDropdown
                            helperText={addErrors.branch}
                            error={!!addErrors.branch}
                            setFormValues={setFormValues}
                            isFilter={false}
                            selectedBranch={selectedBranch}
                            setSelectedBranch={setSelectedBranch}
                        />
                    </Box>

                    <Box width={isTablet ? '100%' : '32%'}>
                        <CustomInput
                            label={t('name')}
                            placeholder={t('nameHere')}
                            name="name"
                            type='text'
                            value={formValues.name}
                            error={!!addErrors.name}
                            helperText={addErrors.name}
                            onChange={handleChange}
                        />
                    </Box>

                    <Box width={isTablet ? '100%' : '32%'}>
                        <CustomInput
                            label={t('ownerName')}
                            placeholder={t('ownerNameHere')}
                            name="ownerName"
                            type='text'
                            value={formValues.ownerName}
                            error={!!addErrors.ownerName}
                            helperText={addErrors.ownerName}
                            onChange={handleChange}
                        />
                    </Box>


                </Box>


                {/* row 2 */}
                <Box sx={{
                    display: isTablet ? 'unset' : 'flex',
                    justifyContent: 'space-between'
                }}>
                    <Box width={isTablet ? '100%' : '32%'}>
                        <CustomInput
                            label={t('phone')}
                            placeholder={t('phoneHere')}
                            name="phone"
                            type='text'
                            value={formValues.phone}
                            error={!!addErrors.phone}
                            helperText={addErrors.phone}
                            onChange={handleChange}
                        />
                    </Box>


                    <Box width={isTablet ? '100%' : '32%'}>
                        <CustomInput
                            label={t('phoneTwo')}
                            placeholder={t('phoneTwoHere')}
                            name="phoneTwo"
                            type='text'
                            value={formValues.phoneTwo}
                            onChange={handleChange}
                        />
                    </Box>

                    <Box width={isTablet ? '100%' : '32%'}>
                        <CustomInput
                            label={t('instgram')}
                            placeholder={t('instgramHere')}
                            name="instgram"
                            type='text'
                            value={formValues.instgram}
                            onChange={handleChange}
                        />
                    </Box>
                </Box>

                {/* row 3 */}
                <Box sx={{
                    display: isTablet ? 'unset' : 'flex',
                    justifyContent: 'space-between'
                }}>

                    <Box width={isTablet ? '100%' : '32%'}>
                        <CustomInput
                            label={t('password')}
                            placeholder="******"
                            name="password"
                            type="password"
                            value={formValues.password}
                            onChange={handleChange}
                            error={!!addErrors.password}
                            helperText={addErrors.password}
                        />
                    </Box>

                    <Box width={isTablet ? '100%' : '32%'}>
                        <CustomInput
                            label={t('confirmPassword')}
                            placeholder="******"
                            name="confirmPassword"
                            type="password"
                            value={formValues.confirmPassword}
                            onChange={handleChange}
                            error={!!addErrors.confirmPassword}
                            helperText={addErrors.confirmPassword}
                        />
                    </Box>

                    <Box width={isTablet ? '100%' : '32%'}>
                        <CustomInput
                            label={t('facebook')}
                            placeholder={t('facebookHere')}
                            name="facebook"
                            type='text'
                            value={formValues.facebook}
                            onChange={handleChange}
                        />
                    </Box>
                </Box>

                {/* row 4 */}
                <Box width={'100%'}>
                    <CustomInput
                        label={t('note')}
                        placeholder={t('noteHere')}
                        name="note"
                        type='text'
                        value={formValues.note}
                        onChange={handleChange}
                    />
                </Box>

                {/* row 5 images */}
                <Box
                    sx={{
                        display: isTablet ? 'unset' : 'flex',
                        justifyContent: 'space-between',
                        width: '100%'
                    }}>
                    <Box sx={{ width: isTablet ? '100%' : '48%' }}>
                        <ImageUpload
                            label={t('uploadImage')}
                            name={t('image')}
                            value={selectedImage}
                            onChange={handleImageChange}
                        />
                    </Box>

                    <Box sx={{ width: isTablet ? '100%' : '48%' }}>
                        <ImageUpload
                            label={t('uploadIDImage')}
                            name={t('IDimage')}
                            value={selectedIDImage}
                            onChange={handleIDImageChange}
                        />
                    </Box>

                </Box>

                {/* row 6 prices */}
                <CityPageListForm
                    formValues={formValues}
                    setFormValues={setFormValues}
                />

                {addErrors.general && (
                    <Typography align="center" color="error" variant="body2" sx={{ mt: 2 }}>
                        {addErrors.general}
                    </Typography>
                )}

                <Box sx={{
                    display: 'flex', justifyContent: 'center', alignItems: 'center',
                    mt: addErrors.general ? 2 : 4
                }}>
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        sx={{ textTransform: 'none', width: 140, height: 40 }}
                    >
                        {isLoading ? <CircularProgress color="secondary" size={28} /> :
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                <p>{t('add')}</p>
                                <AddIcon fontSize="small" sx={{ mx: 0.4 }} />
                            </Box>
                        }
                    </Button>
                </Box>
            </Box>
        </Box>
    );
}

export default AddPage;